import type {
  JourneyDayEntry,
  JourneyDetail,
  JourneyExpense,
  JourneyExpenseSource,
  JourneyMapPhotosResponse,
  JourneyPhotoMeta,
  JourneyPlace,
  JourneyPlaceValidateResult,
  JourneyPublicDetail,
  JourneyShareState,
  JourneySummary,
} from '../../types/journey';
import { awaitJob, fetchAsyncJob } from './asyncJob';
import { API_URL, fetchApi, fetchWithAuth } from './client';

export type {
  JourneyDayEntry,
  JourneyDetail,
  JourneyExpense,
  JourneyExpenseSource,
  JourneyMapPhotoItem,
  JourneyMapPhotosResponse,
  JourneyPhotoMeta,
  JourneyPlace,
  JourneyPlaceValidateResult,
  JourneyPublicDetail,
  JourneyShareState,
  JourneySummary,
} from '../../types/journey';

export const getJourneys = async (): Promise<JourneySummary[]> => {
  return fetchApi<JourneySummary[]>(`${API_URL}/journeys`);
};

export const createJourney = async (body: {
  tag: string;
  dateFrom?: string | null;
  dateTo?: string | null;
}): Promise<JourneySummary> => {
  return fetchApi<JourneySummary>(`${API_URL}/journeys`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const updateJourneyMeta = async (
  tag: string,
  body: {
    dateFrom?: string | null;
    dateTo?: string | null;
    iconId?: string | null;
    countryIso2List?: string[];
  },
): Promise<JourneySummary> => {
  return fetchApi<JourneySummary>(
    `${API_URL}/journeys/by-tag/meta?tag=${encodeURIComponent(tag)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
};

export const renameJourneyTag = async (tag: string, newTag: string): Promise<JourneySummary> => {
  return fetchApi<JourneySummary>(
    `${API_URL}/journeys/by-tag/rename?tag=${encodeURIComponent(tag)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newTag }),
    },
  );
};

export const getJourneyByTag = async (tag: string): Promise<JourneyDetail> => {
  return fetchApi<JourneyDetail>(`${API_URL}/journeys/by-tag?tag=${encodeURIComponent(tag)}`);
};

export const upsertJourneyDay = async (tag: string, entryDate: string, content: string): Promise<JourneyDayEntry> => {
  return fetchApi<JourneyDayEntry>(`${API_URL}/journeys/by-tag/days?tag=${encodeURIComponent(tag)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entryDate, content }),
  });
};

export const deleteJourneyDay = async (tag: string, entryDate: string): Promise<void> => {
  await fetchApi<void>(
    `${API_URL}/journeys/by-tag/days?tag=${encodeURIComponent(tag)}&entryDate=${encodeURIComponent(entryDate)}`,
    { method: 'DELETE' },
  );
};

export const uploadJourneyPhoto = async (
  tag: string,
  entryDate: string,
  file: File,
): Promise<JourneyPhotoMeta> => {
  const body = new FormData();
  body.append('file', file);
  const response = await fetchWithAuth(
    `${API_URL}/journeys/by-tag/days/photos?tag=${encodeURIComponent(tag)}&entryDate=${encodeURIComponent(entryDate)}`,
    { method: 'POST', body },
  );
  if (response.status === 401) {
    throw new Error('Session expired');
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const detail = errorData.detail;
    let msg = errorData.message || `HTTP ${response.status}`;
    if (typeof detail === 'string') msg = detail;
    else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
    throw new Error(msg);
  }
  return response.json();
};

export const deleteJourneyPhoto = async (photoId: number): Promise<void> => {
  await fetchApi<void>(`${API_URL}/journeys/photos/${photoId}`, { method: 'DELETE' });
};

export const getJourneyMapPhotos = async (tag?: string | null): Promise<JourneyMapPhotosResponse> => {
  const q = tag ? `?tag=${encodeURIComponent(tag)}` : '';
  return fetchApi<JourneyMapPhotosResponse>(`${API_URL}/journeys/map-photos${q}`);
};

export const updateJourneyPhotoGeo = async (
  photoId: number,
  body: { lat: number; lng: number } | { clear: true },
): Promise<JourneyPhotoMeta> => {
  return fetchApi<JourneyPhotoMeta>(`${API_URL}/journeys/photos/${photoId}/geo`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const getJourneyShare = async (tag: string): Promise<JourneyShareState> => {
  return fetchApi<JourneyShareState>(
    `${API_URL}/journeys/by-tag/share?tag=${encodeURIComponent(tag)}`,
  );
};

export const setJourneyShare = async (tag: string, enabled: boolean): Promise<JourneyShareState> => {
  return fetchApi<JourneyShareState>(
    `${API_URL}/journeys/by-tag/share?tag=${encodeURIComponent(tag)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    },
  );
};

export const rotateJourneyShare = async (tag: string): Promise<JourneyShareState> => {
  return fetchApi<JourneyShareState>(
    `${API_URL}/journeys/by-tag/share/rotate?tag=${encodeURIComponent(tag)}`,
    { method: 'POST' },
  );
};

export const getPublicJourney = async (token: string): Promise<JourneyPublicDetail> => {
  const response = await fetch(`${API_URL}/journeys/public/${encodeURIComponent(token)}`, {
    credentials: 'omit',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      typeof errorData.detail === 'string' ? errorData.detail : `HTTP ${response.status}`,
    );
  }
  return response.json();
};

export const setJourneyCover = async (
  tag: string,
  photoId: number | null,
  focus?: { focusX?: number; focusY?: number },
): Promise<{ coverPhotoId: number | null; coverFocusX?: number; coverFocusY?: number }> => {
  return fetchApi<{ coverPhotoId: number | null; coverFocusX?: number; coverFocusY?: number }>(
    `${API_URL}/journeys/by-tag/cover?tag=${encodeURIComponent(tag)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        photoId,
        focusX: focus?.focusX,
        focusY: focus?.focusY,
      }),
    },
  );
};

export const generateJourneyReport = async (
  tag: string,
  lang?: string,
): Promise<{ tag: string; report: string }> => {
  const params = new URLSearchParams({ tag });
  if (lang) params.set('lang', lang);
  return fetchAsyncJob<{ tag: string; report: string }>(
    `${API_URL}/journeys/by-tag/report?${params}`,
    { method: 'POST' },
    { timeoutMs: 180_000 },
  );
};

export const deleteJourneyReport = async (tag: string): Promise<void> => {
  await fetchApi<void>(`${API_URL}/journeys/by-tag/report?tag=${encodeURIComponent(tag)}`, {
    method: 'DELETE',
  });
};

export const addJourneyExpense = async (
  tag: string,
  body: {
    title: string;
    amount: number;
    currency?: string;
    category?: string;
    entryDate?: string | null;
    note?: string;
  },
): Promise<JourneyExpense> => {
  return fetchApi<JourneyExpense>(`${API_URL}/journeys/by-tag/expenses?tag=${encodeURIComponent(tag)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const updateJourneyExpense = async (
  expenseId: number,
  body: {
    title?: string;
    amount?: number;
    currency?: string;
    category?: string;
    entryDate?: string | null;
    note?: string;
  },
): Promise<JourneyExpense> => {
  return fetchApi<JourneyExpense>(`${API_URL}/journeys/expenses/${expenseId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const listAllJourneyExpenses = async (): Promise<JourneyExpense[]> => {
  const res = await fetchApi<{ items: JourneyExpense[] }>(`${API_URL}/journeys/expenses`);
  return res.items || [];
};

export const deleteJourneyExpense = async (expenseId: number): Promise<void> => {
  await fetchApi<void>(`${API_URL}/journeys/expenses/${expenseId}`, { method: 'DELETE' });
};

export const getJourneyExpenseSources = async (tag: string): Promise<JourneyExpenseSource[]> => {
  const res = await fetchApi<{ items: JourneyExpenseSource[] }>(
    `${API_URL}/journeys/by-tag/expense-sources?tag=${encodeURIComponent(tag)}`,
  );
  return res.items || [];
};

export const importJourneyExpenses = async (
  tag: string,
  items: { type: string; id: number }[],
): Promise<{ imported: JourneyExpense[]; skipped: number }> => {
  return fetchApi(`${API_URL}/journeys/by-tag/expenses/import?tag=${encodeURIComponent(tag)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
};

export const addJourneyPlace = async (
  tag: string,
  body: { name: string; visited?: boolean; entryDate?: string | null },
): Promise<JourneyPlace> => {
  return fetchApi<JourneyPlace>(`${API_URL}/journeys/by-tag/places?tag=${encodeURIComponent(tag)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const updateJourneyPlace = async (
  placeId: number,
  body: { name?: string; visited?: boolean; entryDate?: string | null },
): Promise<JourneyPlace> => {
  return fetchApi<JourneyPlace>(`${API_URL}/journeys/places/${placeId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const deleteJourneyPlace = async (placeId: number): Promise<void> => {
  await fetchApi<void>(`${API_URL}/journeys/places/${placeId}`, { method: 'DELETE' });
};

export const transcribeJourneyAudio = async (
  file: Blob,
  lang?: string,
): Promise<{ text: string }> => {
  const body = new FormData();
  const name = file.type.includes('ogg') ? 'audio.ogg' : 'audio.webm';
  body.append('file', file, name);
  const params = new URLSearchParams();
  if (lang) params.set('lang', lang);
  const qs = params.toString();
  const response = await fetchWithAuth(
    `${API_URL}/journeys/transcribe${qs ? `?${qs}` : ''}`,
    { method: 'POST', body },
  );
  if (response.status === 401) {
    throw new Error('Session expired');
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const detail = errorData.detail;
    let msg = errorData.message || `HTTP ${response.status}`;
    if (typeof detail === 'string') msg = detail;
    else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
    throw new Error(msg);
  }
  const accepted = (await response.json()) as { jobId?: string };
  if (!accepted?.jobId) {
    throw new Error('No jobId in response');
  }
  return awaitJob<{ text: string }>(accepted.jobId, { timeoutMs: 120_000 });
};

export const validateJourneyPlaces = async (
  tag: string,
  body: {
    places?: Array<{ id?: number | null; name: string }>;
    countries?: string[];
  },
  opts?: { apply?: boolean; lang?: string },
): Promise<{ places: JourneyPlaceValidateResult[] }> => {
  const params = new URLSearchParams({ tag });
  if (opts?.apply) params.set('apply', 'true');
  if (opts?.lang) params.set('lang', opts.lang);
  return fetchAsyncJob<{ places: JourneyPlaceValidateResult[] }>(
    `${API_URL}/journeys/by-tag/places/validate?${params}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    { timeoutMs: 120_000 },
  );
};

export const importJourneyPlacesFromPlan = async (
  tag: string,
  planId: number,
  lang?: string,
): Promise<{ added: JourneyPlace[]; skipped: number }> => {
  const params = new URLSearchParams({ tag });
  if (lang) params.set('lang', lang);
  return fetchAsyncJob<{ added: JourneyPlace[]; skipped: number }>(
    `${API_URL}/journeys/by-tag/places/from-plan?${params}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planId }),
    },
    { timeoutMs: 120_000 },
  );
};

/** LLM description for a journey place (eye icon). */
export const getJourneyPlaceInfo = async (
  name: string,
  opts?: { tag?: string; countries?: string[]; lang?: string },
): Promise<{ description?: string | null }> => {
  try {
    const params = new URLSearchParams({ name });
    if (opts?.tag) params.set('tag', opts.tag);
    if (opts?.lang) params.set('lang', opts.lang);
    for (const c of opts?.countries || []) {
      if (c.trim()) params.append('countries', c.trim());
    }
    return await fetchAsyncJob<{ description?: string | null }>(
      `${API_URL}/journeys/place-info?${params}`,
      { method: 'POST' },
      { timeoutMs: 120_000 },
    );
  } catch {
    return {};
  }
};
